"use client";

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { AlertTriangle, RefreshCw, Upload } from 'lucide-react';

interface UploadErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

interface UploadErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class UploadErrorBoundary extends React.Component<
  UploadErrorBoundaryProps,
  UploadErrorBoundaryState
> {
  constructor(props: UploadErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): UploadErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Upload error caught by boundary:', error, errorInfo);
    
    // Check if this is an external script error
    const isExternalError = error.message?.includes('enabledFeatures') || 
                           error.message?.includes('args.site') ||
                           error.message?.includes('isFeatureBroken') ||
                           error.message?.includes('geckoformboundary');
    
    if (isExternalError) {
      console.warn('External script error detected in upload - ignoring');
      // Reset the error state for external script errors
      this.setState({ hasError: false, error: undefined });
      return;
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error!} resetError={this.resetError} />;
      }

      return (
        <div className="flex items-center justify-center p-8">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <Upload className="h-5 w-5" />
                Upload Error
              </CardTitle>
              <CardDescription>
                An error occurred during the upload process. This might be caused by an external script or browser extension.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="p-3 bg-muted rounded-md">
                  <p className="text-sm font-mono text-muted-foreground">
                    {this.state.error.message}
                  </p>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={this.resetError} variant="outline" className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </Button>
                <Button onClick={() => window.location.reload()}>
                  Reload Page
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook version for functional components
export function useUploadErrorHandler() {
  return (error: Error, errorInfo: React.ErrorInfo) => {
    console.error('Upload error caught by useUploadErrorHandler:', error, errorInfo);
    
    // Check if this is an external script error
    const isExternalError = error.message?.includes('enabledFeatures') || 
                           error.message?.includes('args.site') ||
                           error.message?.includes('isFeatureBroken') ||
                           error.message?.includes('geckoformboundary');
    
    if (isExternalError) {
      console.warn('External script error detected in upload - ignoring');
      return;
    }
    
    // You can add additional error reporting here
    // Example: Sentry.captureException(error, { extra: errorInfo });
  };
}
